import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SEALED_QUERY } from '../lib/productTypes';
import { ALL_GAMES, DEFAULT_GAME, fetchSearchJson, showGameBadges, gameShortLabel, headerSearchGame } from '../lib/games';

function SearchBarNav() {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const searchRef = useRef(null);
    const abortRef = useRef(null);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const navigate = useNavigate();
    // WO-26: the dropdown searches every active game (tcg=all; an API without it is asked for Pokemon instead), or the
    // game picked on the current page (?game=), so it suggests what the search page will show.
    const location = useLocation();
    const tcg = headerSearchGame(location.search);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setActiveIndex(0);
    }, [suggestions]);

    useEffect(() => {
        const debounceTimeout = setTimeout(() => {
            const term = query.trim();
            if (!term) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            if (abortRef.current) {
                abortRef.current.abort();
            }

            const controller = new AbortController();
            abortRef.current = controller;

            const cardsReq = fetchSearchJson(
                `${apiUrl}/api/search/suggest?query=${encodeURIComponent(term)}&limit=5&tcg=${tcg}&types=card,set,series`,
                { signal: controller.signal }
            );
            // WO-20: sealed products in the dropdown too (first when the query names a sealed kind, e.g. "etb").
            const sealedReq = term.length >= 3
                ? fetchSearchJson(`${apiUrl}/api/search/products?query=${encodeURIComponent(term)}&limit=3${tcg === DEFAULT_GAME ? '' : `&tcg=${tcg}`}`, { signal: controller.signal })
                    .catch((err) => { if (err.name === 'AbortError') throw err; return { results: [] }; })
                : Promise.resolve({ results: [] });

            Promise.all([cardsReq, sealedReq])
                .then(([data, sealed]) => {
                    const cards = data.results || [];
                    const products = (sealed.results || []).map((p) => ({
                        type: 'sealed', id: p.product_id, label: p.name, badge: p.type_name || 'sealed', game: p.tcg,
                    }));
                    const results = SEALED_QUERY.test(term) ? [...products, ...cards] : [...cards, ...products];
                    // WO-26: name the game on every row once a non-Pokemon row is in the list
                    results.forEach((r) => { r.gameLabel = null; });
                    if (showGameBadges(results, 'game')) {
                        results.forEach((r) => { if (r.game && !r.is_query) r.gameLabel = gameShortLabel(r.game); });
                    }
                    setSuggestions(results);
                    setShowSuggestions(results.length > 0);
                })
                .catch((error) => {
                    if (error.name === 'AbortError') return;
                    console.error('Error loading suggestions:', error);
                    setSuggestions([]);
                    setShowSuggestions(false);
                });
        }, 150);

        return () => clearTimeout(debounceTimeout);
    }, [query, apiUrl, tcg]);

    const handleInputChange = (e) => {
        setQuery(e.target.value);
    };

    const navigateToSearch = (term) => {
        navigate(`/search/${encodeURIComponent(term)}${tcg !== ALL_GAMES ? `?game=${encodeURIComponent(tcg)}` : ''}`);
    };

    const handleSuggestionClick = (item) => {
        if (!item) return;
        if (item.is_query && item.label) {
            navigateToSearch(item.label);
            return;
        }
        if (item.type === 'card' && item.id) {
            navigate(`/card/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.type === 'sealed' && item.id) {
            navigate(`/product/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.type === 'set' && item.id) {
            navigate(`/sets/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.type === 'series' && item.id) {
            navigate(`/series/${encodeURIComponent(item.id)}`);
            return;
        }
        if (item.label) {
            navigateToSearch(item.label);
            return;
        }
        if (query.trim()) {
            navigateToSearch(query);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && query.trim()) {
            e.preventDefault();
            navigateToSearch(query);
            return;
        }

        if (!showSuggestions || suggestions.length === 0) {
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prevIndex) =>
                prevIndex === suggestions.length - 1 ? 0 : prevIndex + 1
            );
        }

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prevIndex) =>
                prevIndex === 0 ? suggestions.length - 1 : prevIndex - 1
            );
        }

        if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <div className="relative w-full md:w-64" ref={searchRef}>
            <input
                id="search-input"
                type="text"
                placeholder="Search cards, sealed, sets..."
                className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-sm text-cream placeholder:text-cream/50 focus:outline-none focus:ring-1 focus:ring-purple/50"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => query.trim() && suggestions.length > 0 && setShowSuggestions(true)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.3-4.3"></path>
            </svg>

            {showSuggestions && (
                <div className="absolute left-0 right-0 mt-1 bg-dark/95 border border-purple/30 rounded-md shadow-lg max-h-[60vh] md:max-h-60 overflow-y-auto z-50">
                    {suggestions.map((item, index) => (
                        <div
                            key={`${item.type || 'query'}-${item.id || item.label}-${index}`}
                            className={`px-3 py-2 cursor-pointer flex justify-between items-center ${index === activeIndex ? 'bg-purple/20' : 'hover:bg-purple/10'}`}
                            onClick={() => handleSuggestionClick(item)}
                            onMouseEnter={() => setActiveIndex(index)}
                        >
                            <span className="min-w-0">
                                <span className="block text-cream text-sm line-clamp-2" title={item.label}>{item.label}</span>
                                {item.gameLabel && (
                                    <span className="block text-[11px] text-cream/50 leading-tight" data-testid="suggest-game">{item.gameLabel}</span>
                                )}
                            </span>
                            {item.type && !item.is_query && (
                                <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ml-2 ${item.type === 'sealed' ? 'bg-skyblue/15 text-skyblue' : 'bg-purple/30 text-purple'}`}>{item.badge || item.type}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default SearchBarNav;
